import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MapPin, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import GlobalLoader from './ui/GlobalLoader';

// Fix Leaflet default marker icons
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface LocationPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (location: { latitude: number; longitude: number; address: string }) => void;
  initialPosition?: [number, number] | null;
  initialAddress?: string;
}

// Map click handler component
function LocationSelector({ 
  onLocationSelect 
}: { 
  onLocationSelect: (lat: number, lng: number) => void 
}) {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

export function LocationPickerModal({
  isOpen,
  onClose,
  onConfirm,
  initialPosition,
  initialAddress = '',
}: LocationPickerModalProps) {
  const [selectedPosition, setSelectedPosition] = useState<[number, number] | null>(
    initialPosition || [40.0583, -74.4057] // Default to New Jersey (Trenton area)
  );
  const [address, setAddress] = useState(initialAddress || 'Trenton, New Jersey, USA');
  const [mapCenter, setMapCenter] = useState<[number, number]>(
    initialPosition || [40.0583, -74.4057] // Default to New Jersey (Trenton area)
  );
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Array<{ display_name: string; lat: string; lon: string }>>([]);

  // Get current location on mount (only if no initial position provided)
  useEffect(() => {
    if (!initialPosition && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setMapCenter([latitude, longitude]);
          setSelectedPosition([latitude, longitude]);
          // Get address for current location
          getAddressFromCoordinates(latitude, longitude).then(setAddress);
        },
        (error) => {
          console.error('Error getting location:', error);
          // Keep default New Jersey location if geolocation fails
        }
      );
    }
  }, [initialPosition]);

  // Reverse geocoding - get address from coordinates
  const getAddressFromCoordinates = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await response.json();
      return data.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }
  };

  // Handle map click
  const handleLocationSelect = async (lat: number, lng: number) => {
    setIsLoadingLocation(true);
    setSelectedPosition([lat, lng]);
    // Don't update mapCenter to avoid "bumping" - let the marker show the selection
    
    // Get address from coordinates
    const addressText = await getAddressFromCoordinates(lat, lng);
    setAddress(addressText);
    setIsLoadingLocation(false);
    // toast.success('Location selected!');
  };

  // Use current location
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setSelectedPosition([latitude, longitude]);
        setMapCenter([latitude, longitude]); // Only update center for current location to show user's actual location
        
        const addressText = await getAddressFromCoordinates(latitude, longitude);
        setAddress(addressText);
        setIsLoadingLocation(false);
        // toast.success('Current location set!');
      },
      (error) => {
        console.error('Error getting location:', error);
        toast.error('Failed to get current location');
        setIsLoadingLocation(false);
      }
    );
  };

  // Search address
  const searchAddress = async (query: string) => {
    if (!query || query.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`
      );
      const data = await response.json();
      setSuggestions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error searching address:', error);
      setSuggestions([]);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    searchAddress(value);
  };

  const handleSuggestionSelect = (suggestion: { display_name: string; lat: string; lon: string }) => {
    const lat = parseFloat(suggestion.lat);
    const lng = parseFloat(suggestion.lon);
    setSelectedPosition([lat, lng]);
    setMapCenter([lat, lng]); // Update center for search results to show the searched location
    setAddress(suggestion.display_name);
    setSearchQuery('');
    setSuggestions([]);
    toast.success('Location set from search!');
  };

  const handleConfirm = () => {
    if (!selectedPosition) {
      toast.error('Please select a location on the map');
      return;
    }

    onConfirm({
      latitude: selectedPosition[0],
      longitude: selectedPosition[1],
      address: address || `${selectedPosition[0].toFixed(6)}, ${selectedPosition[1].toFixed(6)}`,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-green-600" />
            Select Location
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Bar */}
          <div className="space-y-2">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Input
                  type="text"
                  placeholder="Search for an address..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full"
                />
                {suggestions.length > 0 && (
                  <div className="absolute z-[2000] w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {suggestions.map((suggestion, idx) => (
                      <button
                        key={`${suggestion.lat}-${suggestion.lon}-${idx}`}
                        onClick={() => handleSuggestionSelect(suggestion)}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b last:border-b-0"
                      >
                        {suggestion.display_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleUseCurrentLocation}
                disabled={isLoadingLocation}
                className="shrink-0"
              >
                <Navigation className="w-4 h-4 mr-2" />
                Current Location
              </Button>
            </div>
            
            {/* Selected Address Display */}
            {address && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm font-medium text-green-900">Selected Address:</p>
                <p className="text-sm text-green-700 mt-1">{address}</p>
              </div>
            )}
          </div>

          {/* Map */}
          <div className="h-[400px] w-full border border-gray-300 rounded-lg overflow-hidden">
            <MapContainer
              center={mapCenter}
              zoom={13}
              style={{ height: '100%', width: '100%' }}
              key={`${mapCenter[0]}-${mapCenter[1]}`}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <LocationSelector onLocationSelect={handleLocationSelect} />
              {selectedPosition && (
                <Marker position={selectedPosition} />
              )}
            </MapContainer>
          </div>

          <p className="text-sm text-gray-500">
            💡 Click anywhere on the map to select a location, or search for an address above.
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedPosition || isLoadingLocation}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {isLoadingLocation ? 'Loading...' : 'Confirm Location'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Global loading overlay */}
      <GlobalLoader 
        isLoading={isLoadingLocation}
        message="Processing location..."
        subMessage="Please wait while we process your location selection"
      />
    </Dialog>
  );
}

